import React, {useState, useMemo, useRef, useEffect } from 'react';
import classnames from 'classnames';
import { Card, CardBody,Col, Input, Nav, NavItem, NavLink, Row, Spinner } from 'reactstrap';
import ForRentListingTable from './ForRentListingTable';
import ForSaleListingTable from './ForSaleListingTable';
import SoldListingTable from './SoldListingTable';
import RentedListingTable from './RentedListingTable';
import ArchivedListingTable from './ArchivedListingTable';
import { useQuery } from '@tanstack/react-query';
import { debounce } from 'lodash';
import { builderPropertiesListing } from 'endpoints/builder/builderDetails.action';
import { useSelector } from 'react-redux';
import { RootState } from 'slices/store';


const PropertyListingTable = () => {
    const [isFirstMount, setIsFirstMount] = useState(true);
    const builderId = useSelector((state: RootState) => state.Builder.selectedBuilderId);
    const [activeTab, setActiveTab] = useState('RentListings');
    const [searchInput, setSearchInput] = useState('');
    const [searchTerm, setSearchTerm] = useState('');
    const [currentPage, setCurrentPage] = useState(1);
    const [limit] = useState(10);

    // Debounced search
    const debouncedSearch = useRef(
        debounce((value: string) => {
            setSearchTerm(value);
            setCurrentPage(1);
        }, 500)
    ).current;

    const handleSearch = (value: string) => {
        debouncedSearch(value);
    };

    const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value;
        setSearchInput(value);
        handleSearch(value);
    };

    const clearSearch = () => {
        setSearchInput('');
        setSearchTerm('');
        setCurrentPage(1);
    };

    // Handle pagination
    const handlePageChange = (page: number) => {
        setCurrentPage(page);
    };

    // Handle tab change with currentPage reset
    const handleTabChange = (tabName: string) => {
        setActiveTab(tabName);
        setCurrentPage(1);
    };

    // Build filters according to requirements
    const filters = useMemo(() => {
        const isRent = activeTab === 'RentListings';
        const isSale = activeTab === 'saleListings';
        const property_category = isRent ? 'rent' : isSale ? 'sell' : undefined;

        const base: any = {
            id: builderId || '',
            page: currentPage,
            limit,
        };

        // Only add status if it's not rent or sale
        if (!isRent && !isSale) {
            base.status = activeTab;
        }

        if (property_category) base.property_category = property_category;
        if (searchTerm && searchTerm.trim() !== '') base.search = searchTerm.trim();

        return base;
    }, [activeTab, builderId, currentPage, limit, searchTerm]);

    const { data, isLoading, isFetching, isError, refetch } = useQuery({
        queryKey: ['builderProperties', filters],
        queryFn: () => builderPropertiesListing(filters),
        enabled: !isFirstMount,
        staleTime: 0, 
        gcTime: 0,    
        refetchOnWindowFocus: false,  
        refetchOnMount: true,        
        retry: 3
    });

     // Handle first mount manually
          useEffect(() => {
            if (isFirstMount) {
              setIsFirstMount(false);
              // The query will now be enabled and run automatically
            }
          }, [isFirstMount]);

    const properties = data?.data?.data || [];
    const totalCount = data?.data?.total || 0;
    const totalPages = Math.ceil(totalCount / limit);

    // Render tables using fetched properties; pass loading state
    return (
        <Card>
            <CardBody>
                <Row className="align-items-center mb-3">
                    <Col>
                        <Nav tabs className="nav nav-tabs nav-tabs-custom nav-success nav-justified mb-0">
                            <NavItem>
                                <NavLink
                                    className={classnames({ active: activeTab === 'RentListings' })}
                                    onClick={() => handleTabChange('RentListings')}
                                    style={{ cursor: 'pointer' }}
                                >For Rent Listings
                                    {activeTab === 'RentListings' && totalCount > 0 && (
                                        <span className="badge bg-success ms-2">{totalCount}</span>
                                    )}
                                </NavLink>
                            </NavItem>
                            <NavItem>
                                <NavLink
                                    className={classnames({ active: activeTab === 'saleListings' })}
                                    onClick={() => handleTabChange('saleListings')}
                                    style={{ cursor: 'pointer' }}
                                >For Sale Listings
                                    {activeTab === 'saleListings' && totalCount > 0 && (
                                        <span className="badge bg-success ms-2">{totalCount}</span>
                                    )}
                                </NavLink>
                            </NavItem>
                            <NavItem>
                                <NavLink
                                    className={classnames({ active: activeTab === 'sold' })}
                                    onClick={() => handleTabChange('sold')}
                                    style={{ cursor: 'pointer' }}
                                >Sold Listings
                                { activeTab === 'sold' && totalCount > 0 && (
                                    <span className="badge bg-success ms-2">{totalCount}</span>
                                )}
                                </NavLink>
                            </NavItem>
                            <NavItem>
                                <NavLink
                                    className={classnames({ active: activeTab === 'rented' })}
                                    onClick={() => handleTabChange('rented')}
                                    style={{ cursor: 'pointer' }}
                                >Rented Listings
                                { activeTab === 'rented' && totalCount > 0 && (
                                    <span className="badge bg-success ms-2">{totalCount}</span>
                                )}
                                </NavLink>
                            </NavItem>
                            <NavItem>
                                <NavLink
                                    className={classnames({ active: activeTab === 'archived' })}
                                    onClick={() => handleTabChange('archived')}
                                    style={{ cursor: 'pointer' }}
                                >Archived
                                { activeTab === 'archived' && totalCount > 0 && (
                                    <span className="badge bg-success ms-2">{totalCount}</span>
                                )}
                                </NavLink>
                            </NavItem>
                        </Nav>
                    </Col>
                    <Col md={3} className="text-end">
                        <div className="position-relative">
                            <Input
                                type="text"
                                placeholder="Search Properties..."
                                value={searchInput}
                                onChange={handleSearchChange}
                                className="rounded-2 py-2"
                                style={{
                                    border: '1px solid #e5e7eb',
                                    paddingRight: searchInput ? '40px' : '12px'
                                }}
                            />
                            {searchInput && (
                                <button
                                    type="button"
                                    className="btn btn-link position-absolute end-0 top-50 translate-middle-y"
                                    onClick={clearSearch}
                                    style={{
                                        border: 'none',
                                        background: 'transparent',
                                        padding: '0',
                                        width: '30px',
                                        height: '100%',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        color: '#6c757d',
                                        zIndex: 5
                                    }}
                                >
                                    <i className="ri-close-line"></i>
                                </button>
                            )}
                        </div>
                    </Col>
                </Row>

                {/* Loading State */}
                {(isLoading || isFetching) && (
                    <div className="text-center py-5">
                        <Spinner color="primary" />
                        <p className="mt-2 text-muted">Loading...</p>
                    </div>
                )}

                {/* Error State */}
                {isError && !(isLoading || isFetching) && (
                    <div className="text-center py-5">
                        <div className="text-danger mb-3">
                            <i className="ri-error-warning-line" style={{ fontSize: '3rem' }}></i>
                        </div>
                        <h5 className="text-danger">Failed to load data</h5>
                        <p className="text-muted">Please check your connection and try again.</p>
                        <button
                            className="btn btn-primary"
                            onClick={() => refetch()}
                        >
                            <i className="ri-refresh-line me-1"></i>
                            Retry
                        </button>
                    </div>
                )}

                {/* Content */}
                {!(isLoading || isFetching) && !isError && (
                    <div>
                        {activeTab === 'RentListings' && <ForRentListingTable
                            data={properties}
                            isLoading={isFetching}
                            currentPage={currentPage}
                            totalPages={totalPages}
                            totalCount={totalCount}
                            onPageChange={handlePageChange}
                            onRefresh={refetch}
                        />}
                        {activeTab === 'saleListings' && <ForSaleListingTable
                            data={properties}
                            isLoading={isFetching}
                            currentPage={currentPage}
                            totalPages={totalPages}
                            totalCount={totalCount}
                            onPageChange={handlePageChange}
                            onRefresh={refetch}
                        />}
                        {activeTab === 'sold' && <SoldListingTable
                            data={properties}
                            isLoading={isFetching}
                            currentPage={currentPage}
                            totalPages={totalPages}
                            totalCount={totalCount}
                            onPageChange={handlePageChange}
                            onRefresh={refetch}
                        />}
                        {activeTab === 'rented' && <RentedListingTable
                            data={properties}
                            isLoading={isFetching}
                            currentPage={currentPage}
                            totalPages={totalPages}
                            totalCount={totalCount}
                            onPageChange={handlePageChange}
                            onRefresh={refetch}
                        />}
                        {activeTab === 'archived' && <ArchivedListingTable
                            data={properties}
                            isLoading={isFetching}
                            currentPage={currentPage}
                            totalPages={totalPages}
                            totalCount={totalCount}
                            onPageChange={handlePageChange}
                            onRefresh={refetch}
                        />}
                    </div>
                )}
            </CardBody>
        </Card>
    );
};

export default PropertyListingTable;
